import { Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthReportHeader, { GrowthReportMeta } from '../../components/growth-report/GrowthReportHeader';
import ApiClient from '../../config/ApiClient';
import { FadedPaper } from '../../theme/StyledFields';

export interface GrowthScoreRow {
    domain_key: string;
    label: string;
    pre_mean: number | null;
    post_mean: number | null;
    delta_mean: number | null;
    sample_size: number;
}

export interface GrowthReportData {
    header: {
        organization_name: string;
        report_date: string;
        pairs_count: number;
    };
    overall: GrowthScoreRow;
    domains: GrowthScoreRow[];
    report_meta?: GrowthReportMeta;
}

function formatMean(v: number | null): string {
    if (v == null) return '—';
    return v.toFixed(2);
}

function DeltaChip({ delta }: { delta: number | null }) {
    if (delta == null) {
        return <Typography component="span" color="text.secondary">—</Typography>;
    }
    const label = delta > 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2);
    const color = delta > 0 ? 'success' : delta < 0 ? 'error' : 'default';
    return <Chip size="small" label={label} color={color} variant={delta === 0 ? 'outlined' : 'filled'} />;
}

function defaultMetaForWeek(week: number): GrowthReportMeta {
    return {
        week,
        title: `Week ${week} organization growth report`,
        context_lines: [],
    };
}

const REPORT_WEEK_TABS = [4, 8, 12] as const;

const GrowthReportSection: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const weekNum = useMemo(() => {
        const n = parseInt(searchParams.get('week') || '12', 10);
        if (Number.isNaN(n)) return 12;
        return Math.min(12, Math.max(1, n));
    }, [searchParams]);

    const [data, setData] = useState<GrowthReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        ApiClient.get<GrowthReportData>(`/api/reports/growth/week/${weekNum}/`)
            .then((res) => {
                if (!res.success || res.data == null) {
                    const detail = (res.errors as { detail?: string } | undefined)?.detail;
                    setError(detail || res.error || 'Failed to load growth report');
                    setData(null);
                    return;
                }
                setData(res.data);
            })
            .catch(() => {
                setError('Failed to load growth report');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [weekNum]);

    const setWeek = (w: number) => {
        const next = new URLSearchParams(searchParams);
        next.set('week', String(w));
        setSearchParams(next, { replace: true });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mb: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ mb: 3 }}>
                <Alert severity="warning">No growth data available.</Alert>
            </Box>
        );
    }

    const meta: GrowthReportMeta = data.report_meta ?? defaultMetaForWeek(weekNum);
    const rows: GrowthScoreRow[] = [data.overall, ...data.domains];

    return (
        <Box sx={{ mb: 1 }} data-testid="section-growth">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ButtonGroup size="small" variant="outlined" aria-label="Select report week">
                    {REPORT_WEEK_TABS.map((w) => (
                        <Button
                            key={w}
                            variant={w === weekNum ? 'contained' : 'outlined'}
                            onClick={() => setWeek(w)}
                            data-testid={`growth-report-org-tab-week-${w}`}
                        >
                            Week {w}
                        </Button>
                    ))}
                </ButtonGroup>
            </Box>

            <FadedPaper sx={{ p: 3, mb: 3 }}>
                <GrowthReportHeader
                    meta={meta}
                    organizationName={data.header.organization_name}
                    reportDate={data.header.report_date}
                    pairsCount={data.header.pairs_count}
                />
            </FadedPaper>

            <FadedPaper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Pre vs post — mean scores and change
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Domain</TableCell>
                                <TableCell align="right">Mean pre</TableCell>
                                <TableCell align="right">Mean post</TableCell>
                                <TableCell align="right">Mean delta (post − pre)</TableCell>
                                <TableCell align="right">n</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow
                                    key={row.domain_key}
                                    sx={
                                        row.domain_key === data.overall.domain_key
                                            ? { bgcolor: 'action.hover' }
                                            : undefined
                                    }
                                >
                                    <TableCell>
                                        <Typography fontWeight={row.domain_key === data.overall.domain_key ? 600 : 400}>
                                            {row.label}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatMean(row.pre_mean)}</TableCell>
                                    <TableCell align="right">{formatMean(row.post_mean)}</TableCell>
                                    <TableCell align="right">
                                        <DeltaChip delta={row.delta_mean} />
                                    </TableCell>
                                    <TableCell align="right">{row.sample_size}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </FadedPaper>
        </Box>
    );
};

export default GrowthReportSection;
